<template>
	<v-tabs :tabless="true" :update-hash="false" class="quote-breakdown tabs--switch">
		<v-tab id="quote-breakdown__summary" label="Quote Summary">
			<header>
				<h1 class="beta heavy">Quote Summary</h1>
			</header>
			<table class="mt-1">
				<tbody v-if="quote.calculation.sale">
					<tr>
						<th align="left" colspan="2" scope="rowgroup">
							<div class="flex items-center content-between gap-1">
								<span class="beta">Sale</span>
								<button
									class="button button--secondary--small"
									@click.prevent="showInModal('sale')">Breakdown
								</button>
							</div>
						</th>
					</tr>
					<tr>
						<th align="left" class="light" scope="row">Fees</th>
						<td align="right" v-html="$present('price', quote.calculation.sale.allFeesSubTotal)"/>
					</tr>
					<tr>
						<th align="left" class="light" scope="row">Disbursements</th>
						<td align="right" v-html="$present('price', quote.calculation.sale.disbursementsSubTotal)"/>
					</tr>
					<tr>
						<th align="left" scope="row">Sale Sub Total</th>
						<td
							align="right" class="heavy"
							v-html="$present('price', quote.calculation.sale.subTotal)"/>
					</tr>
				</tbody>
				<tbody v-if="quote.calculation.purchase">
					<tr>
						<th align="left" colspan="2" scope="rowgroup">
							<div class="flex items-center content-between gap-1">
								<span class="beta">Purchase</span>
								<button
									class="button button--secondary--small"
									@click.prevent="showInModal('purchase')">Breakdown
								</button>
							</div>
						</th>
					</tr>
					<tr>
						<th align="left" class="light" scope="row">Fees</th>
						<td align="right" v-html="$present('price', quote.calculation.purchase.allFeesSubTotal)"/>
					</tr>
					<tr>
						<th align="left" class="light" scope="row">Disbursements</th>
						<td align="right" v-html="$present('price', quote.calculation.purchase.disbursementsSubTotal)"/>
					</tr>
					<tr>
						<th align="left" scope="row">Purchase Sub Total</th>
						<td
							align="right" class="heavy"
							v-html="$present('price', quote.calculation.purchase.subTotal)"/>
					</tr>
				</tbody>
				<tbody v-if="quote.calculation.remortgage">
					<tr>
						<th align="left" colspan="2" scope="rowgroup">
							<div class="flex items-center content-between gap-1">
								<span class="beta">Remortgage</span>
								<button
									class="button button--secondary--small"
									@click.prevent="showInModal('remortgage')">Breakdown
								</button>
							</div>
						</th>
					</tr>
					<tr>
						<th align="left" class="light" scope="row">Fees</th>
						<td align="right" v-html="$present('price', quote.calculation.remortgage.allFeesSubTotal)"/>
					</tr>
					<tr>
						<th align="left" class="light" scope="row">Disbursements</th>
						<td
							align="right"
							v-html="$present('price', quote.calculation.remortgage.disbursementsSubTotal)"/>
					</tr>
					<tr>
						<th align="left" scope="row">Remortgage Sub Total</th>
						<td
							align="right" class="heavy"
							v-html="$present('price', quote.calculation.remortgage.subTotal)"/>
					</tr>
				</tbody>
				<tbody v-if="quote.calculation.other.disbursements">
					<tr>
						<th align="left" class="beta" colspan="2" scope="rowgroup">Other Disbursements</th>
					</tr>
					<tr
						v-for="(disbursement, disbursementIndex) in quote.calculation.other.disbursements"
						v-if="disbursement.price.units">
						<th align="left" class="light" scope="row">
							<span v-html="disbursement.label"/>
							<span
								v-if="disbursement.qty > 1">x {{ disbursement.qty }} @ {{ $present("price", disbursement.price) }}</span>
						</th>
						<td align="right" v-html="$present('price', disbursement.subTotal)"/>
					</tr>
					<tr>
						<th align="left" scope="row">ID Checks Total</th>
						<td
							align="right" class="heavy"
							v-html="$present('price', quote.calculation.other.disbursementsSubTotal)"/>
					</tr>
				</tbody>
				<tbody>
					<tr>
						<th align="left" scope="row">VAT</th>
						<td
							align="right"
							class="heavy"
							v-html="$present('price', quote.calculation.tax)"/>
					</tr>
					<tr>
						<th align="left" class="beta" scope="row">Total</th>
						<td align="right" class="beta heavy" v-html="$present('price', quote.calculation.total)"/>
					</tr>
				</tbody>
			</table>
		</v-tab>
		<v-tab
			v-if="quote.calculation.sale" id="quote-breakdown--sale"
			label="Sale Quote">
			<header class="flex items-center content-between gap-1">
				<h1 class="beta heavy">Sale Breakdown</h1>
				<button
					class="button button--secondary--small"
					@click.prevent="showInModal('summary')">Back To Summary
				</button>
			</header>
			<table class="mt-1">
				<tbody v-if="quote.calculation.sale.fees">
					<tr>
						<th align="left" class="beta" colspan="2" scope="rowgroup">Sale Fees</th>
					</tr>
					<tr>
						<th align="left" class="light" scope="row">Legal Fees</th>
						<td align="right" v-html="$present('price', quote.calculation.sale.legalFeesSubTotal)"/>
					</tr>
					<template v-if="$authed()">
						<tr v-if="quote.calculation.sale.calmInstructionCommission && ($authed().isStaff || quote.service_provider_id === $authed().service_provider.id)">
							<th align="left" class="light" scope="row">Including the Calm instruction commission</th>
							<td
								align="right"
								v-html="$present('price', quote.calculation.sale.calmInstructionCommission)"/>
						</tr>
						<tr v-if="quote.calculation.sale.introducerCommission">
							<th align="left" class="light" scope="row">Including an introducer commission</th>
							<td
								align="right"
								v-html="$present('price', quote.calculation.sale.introducerCommission)"/>
						</tr>
					</template>
					<tr v-for="(fee, feeIndex) in quote.calculation.sale.fees" v-if="fee.price.units">
						<th align="left" class="light" scope="row">
							<span v-html="fee.label"/>
							<span
								v-if="fee.qty > 1">x {{ fee.qty }} @ {{ $present("price", fee.price) }}</span>
						</th>
						<td align="right" v-html="$present('price', fee.subTotal)"/>
					</tr>
					<tr>
						<th align="left" scope="row">Sale Fees Sub Total</th>
						<td
							align="right" class="heavy"
							v-html="$present('price', quote.calculation.sale.allFeesSubTotal)"/>
					</tr>
				</tbody>
				<tbody v-if="quote.calculation.sale.disbursements">
					<tr>
						<th align="left" class="beta" colspan="2" scope="rowgroup">Sale Disbursements</th>
					</tr>
					<tr
						v-for="(disbursement, disbursementIndex) in quote.calculation.sale.disbursements"
						v-if="disbursement.price.units">
						<th align="left" class="light" scope="row">
							<span v-html="disbursement.label"/>
							<span
								v-if="disbursement.qty > 1">x {{ disbursement.qty }} @ {{ $present("price", disbursement.price) }}</span>
						</th>
						<td align="right" v-html="$present('price', disbursement.subTotal)"/>
					</tr>
					<tr>
						<th align="left" scope="row">Sale Disbursements Total</th>
						<td
							align="right"
							class="heavy"
							v-html="$present('price', quote.calculation.sale.disbursementsSubTotal)"/>
					</tr>
				</tbody>
				<tbody>
					<tr>
						<th align="left" scope="row">VAT</th>
						<td
							align="right"
							class="heavy"
							v-html="$present('price', quote.calculation.sale.allFeesTax)"/>
					</tr>
					<tr>
						<th align="left" class="beta" scope="row">Sale Total</th>
						<td
							align="right"
							class="beta heavy"
							v-html="$present('price', quote.calculation.sale.total)"/>
					</tr>
				</tbody>
			</table>
			<ul class="actions actions--right">
				<li>
					<button
						class="button button--secondary--small"
						@click.prevent="showInModal('summary')">Back To Summary
					</button>
				</li>
			</ul>
			<dl class="detail-list">
				<div class="detail-list__item">
					<dt>Property address</dt>
					<dd>
						<Address :address="quote.service_requirements.sale.address"/>
					</dd>
				</div>
				<div class="detail-list__item">
					<dt>Sale property price</dt>
					<dd v-html="$present('priceWholes', quote.service_requirements.sale.property_price)"/>
				</div>
				<div class="detail-list__item">
					<dt>Number of sellers</dt>
					<dd v-html="quote.service_requirements.sale.number_of_people"/>
				</div>

				<div class="detail-list__item--icon">
					<dt>Mortgage</dt>
					<dd>
						<bool-icon :value="quote.service_requirements.sale.mortgage"/>
					</dd>
				</div>
				<div v-if="quote.service_requirements.sale.mortgage" class="detail-list__item--icon">
					<dt>Lender</dt>
					<dd>
						<bool-icon v-if="!quote.service_requirements.sale.mortgage" :value="false"/>
						<span
							v-if="!quote.service_requirements.sale.lender">Unknown</span>
						<span
							v-if="quote.service_requirements.sale.lender"
							v-html="quote.service_requirements.sale.lender.name"/>
					</dd>
				</div>
				<div class="detail-list__item--icon">
					<dt>Leasehold</dt>
					<dd>
						<bool-icon :value="quote.service_requirements.sale.leasehold"/>
					</dd>
				</div>
				<div class="detail-list__item--icon">
					<dt>Purchase scheme</dt>
					<dd>
						<bool-icon v-if="!quote.service_requirements.sale.has_scheme" :value="false"/>
						<span
							v-if="quote.service_requirements.sale.has_scheme"
							v-html="$enum('PurchaseSchemeEnum').display(quote.service_requirements.sale.scheme)"/>
					</dd>
				</div>
			</dl>
			<ul class="actions actions--right">
				<li>
					<button
						class="button button--secondary--small"
						@click.prevent="showInModal('summary')">Back To Summary
					</button>
				</li>
			</ul>
		</v-tab>
		<v-tab
			v-if="quote.calculation.purchase" id="quote-breakdown--purchase"
			label="Purchase Quote">
			<header class="flex items-center content-between gap-1">
				<h1 class="beta heavy">Purchase Breakdown</h1>
				<button
					class="button button--secondary--small"
					@click.prevent="showInModal('summary')">Back To Summary
				</button>
			</header>
			<table class="mt-1">
				<tbody v-if="quote.calculation.purchase.fees">
					<tr>
						<th align="left" class="beta" colspan="2" scope="rowgroup">Purchase Fees</th>
					</tr>
					<tr>
						<th align="left" class="light" scope="row">Legal Fees</th>
						<td
							align="right"
							v-html="$present('price', quote.calculation.purchase.legalFeesSubTotal)"/>
					</tr>
					<template v-if="$authed()">
						<tr v-if="quote.calculation.purchase.calmInstructionCommission && ($authed().isStaff || quote.service_provider_id === $authed().service_provider.id)">
							<th align="left" class="light" scope="row">Including the Calm instruction commission</th>
							<td
								align="right"
								v-html="$present('price', quote.calculation.purchase.calmInstructionCommission)"/>
						</tr>
						<tr v-if="quote.calculation.purchase.introducerCommission">
							<th align="left" class="light" scope="row">Including an introducer commission</th>
							<td
								align="right"
								v-html="$present('price', quote.calculation.purchase.introducerCommission)"/>
						</tr>
					</template>
					<tr v-for="(fee, feeIndex) in quote.calculation.purchase.fees" v-if="fee.price.units">
						<th align="left" class="light" scope="row">
							<span v-html="fee.label"/>
							<span
								v-if="fee.qty > 1">x {{ fee.qty }} @ {{ $present("price", fee.price) }}</span>
						</th>
						<td align="right" v-html="$present('price', fee.subTotal)"/>
					</tr>
					<tr>
						<th align="left" scope="row">Purchase Fees Sub Total</th>
						<td
							align="right"
							class="heavy"
							v-html="$present('price', quote.calculation.purchase.allFeesSubTotal)"/>
					</tr>
				</tbody>
				<tbody v-if="quote.calculation.purchase.disbursements">
					<tr>
						<th align="left" class="beta" colspan="2" scope="rowgroup">Purchase Disbursements</th>
					</tr>
					<tr
						v-for="(disbursement, disbursementIndex) in quote.calculation.purchase.disbursements"
						v-if="disbursement.price.units">
						<th align="left" class="light" scope="row">
							<span v-html="disbursement.label"/>
							<span
								v-if="disbursement.qty > 1">x {{ disbursement.qty }} @ {{ $present("price", disbursement.price) }}</span>
						</th>
						<td align="right" v-html="$present('price', disbursement.subTotal)"/>
					</tr>
					<tr>
						<th align="left" scope="row">Purchase Disbursements Total</th>
						<td
							align="right"
							class="heavy"
							v-html="$present('price', quote.calculation.purchase.disbursementsSubTotal)"/>
					</tr>
				</tbody>
				<tbody>
					<tr>
						<th align="left" scope="row">VAT</th>
						<td
							align="right"
							class="heavy"
							v-html="$present('price', quote.calculation.purchase.allFeesTax)"/>
					</tr>
					<tr>
						<th align="left" class="beta" scope="row">Purchase Total</th>
						<td
							align="right"
							class="beta heavy"
							v-html="$present('price', quote.calculation.purchase.total)"/>
					</tr>
				</tbody>
			</table>
			<ul class="actions actions--right">
				<li>
					<button
						class="button button--secondary--small"
						@click.prevent="showInModal('summary')">Back To Summary
					</button>
				</li>
			</ul>
			<dl class="detail-list">
				<div class="detail-list__item">
					<dt>Property Address</dt>
					<dd>
						<Address :address="quote.service_requirements.purchase.address"/>
					</dd>
				</div>
				<div class="detail-list__item">
					<dt>Purchase property price</dt>
					<dd v-html="$present('priceWholes', quote.service_requirements.purchase.property_price)"/>
				</div>
				<div class="detail-list__item">
					<dt>Number of purchasers</dt>
					<dd v-html="quote.service_requirements.purchase.number_of_people"/>
				</div>
				<div class="detail-list__item--icon">
					<dt>Mortgage</dt>
					<dd>
						<bool-icon :value="quote.service_requirements.purchase.mortgage"/>
					</dd>
				</div>
				<div v-if="quote.service_requirements.purchase.mortgage" class="detail-list__item--icon">
					<dt>Lender</dt>
					<dd>
						<bool-icon v-if="!quote.service_requirements.purchase.mortgage" :value="false"/>
						<span
							v-if="!quote.service_requirements.purchase.lender">Unknown</span>
						<span
							v-if="quote.service_requirements.purchase.lender"
							v-html="quote.service_requirements.purchase.lender.name"/>
					</dd>
				</div>
				<div class="detail-list__item--icon">
					<dt>First time buyers</dt>
					<dd>
						<bool-icon :value="quote.service_requirements.purchase.first_time_buyer"/>
					</dd>
				</div>
				<div class="detail-list__item--icon">
					<dt>Second home</dt>
					<dd>
						<bool-icon :value="quote.service_requirements.purchase.second_property"/>
					</dd>
				</div>
				<div class="detail-list__item--icon">
					<dt>New build</dt>
					<dd>
						<bool-icon :value="quote.service_requirements.purchase.new_build"/>
					</dd>
				</div>
				<div class="detail-list__item--icon">
					<dt>Leasehold</dt>
					<dd>
						<bool-icon :value="quote.service_requirements.purchase.leasehold"/>
					</dd>
				</div>
				<div class="detail-list__item--icon">
					<dt>Purchase scheme</dt>
					<dd>
						<bool-icon v-if="!quote.service_requirements.purchase.has_scheme" :value="false"/>
						<span
							v-if="quote.service_requirements.purchase.has_scheme"
							v-html="$enum('PurchaseSchemeEnum').display(quote.service_requirements.purchase.scheme)"/>
					</dd>
				</div>
			</dl>
			<ul class="actions actions--right">
				<li>
					<button
						class="button button--secondary--small"
						@click.prevent="showInModal('summary')">Back To Summary
					</button>
				</li>
			</ul>
		</v-tab>
		<v-tab
			v-if="quote.calculation.remortgage" id="quote-breakdown--remortgage"
			label="Remortgage Quote">
			<header class="flex items-center content-between gap-1">
				<h1 class="beta heavy">Remortgage Breakdown</h1>
				<button
					class="button button--secondary--small"
					@click.prevent="showInModal('summary')">Back To Summary
				</button>
			</header>
			<table class="mt-1">
				<tbody v-if="quote.calculation.remortgage.fees">
					<tr>
						<th align="left" class="beta" colspan="2" scope="rowgroup">Remortgage Fees</th>
					</tr>
					<tr>
						<th align="left" class="light" scope="row">Legal Fees</th>
						<td
							align="right"
							v-html="$present('price', quote.calculation.remortgage.legalFeesSubTotal)"/>
					</tr>
					<template v-if="$authed()">
						<tr v-if="quote.calculation.remortgage.calmInstructionCommission && ($authed().isStaff || quote.service_provider_id === $authed().service_provider.id)">
							<th align="left" class="light" scope="row">Including the Calm instruction commission</th>
							<td
								align="right"
								v-html="$present('price', quote.calculation.remortgage.calmInstructionCommission)"/>
						</tr>
						<tr v-if="quote.calculation.remortgage.introducerCommission">
							<th align="left" class="light" scope="row">Including an introducer commission</th>
							<td
								align="right"
								v-html="$present('price', quote.calculation.remortgage.introducerCommission)"/>
						</tr>
					</template>
					<tr v-for="(fee, feeIndex) in quote.calculation.remortgage.fees" v-if="fee.price.units">
						<th align="left" class="light" scope="row">
							<span v-html="fee.label"/>
							<span
								v-if="fee.qty > 1">x {{ fee.qty }} @ {{ $present("price", fee.price) }}</span>
						</th>
						<td align="right" v-html="$present('price', fee.subTotal)"/>
					</tr>
					<tr>
						<th align="left" scope="row">Remortgage Fees Sub Total</th>
						<td
							align="right"
							class="heavy"
							v-html="$present('price', quote.calculation.remortgage.allFeesSubTotal)"/>
					</tr>
				</tbody>
				<tbody v-if="quote.calculation.remortgage.disbursements">
					<tr>
						<th align="left" class="beta" colspan="2" scope="rowgroup">Remortgage Disbursements</th>
					</tr>
					<tr
						v-for="(disbursement, disbursementIndex) in quote.calculation.remortgage.disbursements"
						v-if="disbursement.price.units">
						<th align="left" class="light" scope="row">
							<span v-html="disbursement.label"/>
							<span
								v-if="disbursement.qty > 1">x {{ disbursement.qty }} @ {{ $present("price", disbursement.price) }}</span>
						</th>
						<td align="right" v-html="$present('price', disbursement.subTotal)"/>
					</tr>
					<tr>
						<th align="left" scope="row">Remortgage Disbursements Total</th>
						<td
							align="right"
							class="heavy"
							v-html="$present('price', quote.calculation.remortgage.disbursementsSubTotal)"/>
					</tr>
				</tbody>
				<tbody>
					<tr>
						<th align="left" scope="row">VAT</th>
						<td
							align="right"
							class="heavy"
							v-html="$present('price', quote.calculation.remortgage.allFeesTax)"/>
					</tr>
					<tr>
						<th align="left" class="beta" scope="row">Remortgage Total</th>
						<td
							align="right"
							class="beta heavy"
							v-html="$present('price', quote.calculation.remortgage.total)"/>
					</tr>
				</tbody>
			</table>
			<ul class="actions actions--right">
				<li>
					<button
						class="button button--secondary--small"
						@click.prevent="showInModal('summary')">Back To Summary
					</button>
				</li>
			</ul>
			<dl class="detail-list">
				<div class="detail-list__item">
					<dt>Property address</dt>
					<dd>
						<Address :address="quote.service_requirements.remortgage.address"/>
					</dd>
				</div>
				<div
					v-if="quote.service_requirements.remortgage.property_price"
					class="detail-list__item">
					<dt>Property price</dt>
					<dd v-html="$present('price', quote.service_requirements.remortgage.property_price)"/>
				</div>
				<div class="detail-list__item">
					<dt>Number of people invloved</dt>
					<dd v-html="quote.service_requirements.remortgage.number_of_people"/>
				</div>
				<div class="detail-list__item">
					<dt>Lender</dt>
					<dd v-if="!quote.service_requirements.remortgage.lender">Unknown</dd>
					<dd
						v-if="quote.service_requirements.remortgage.lender"
						v-html="lenderName(quote.service_requirements.remortgage.lender)"/>
				</div>
				<div class="detail-list__item--icon">
					<dt>Leasehold</dt>
					<dd>
						<bool-icon :value="quote.service_requirements.remortgage.leasehold"/>
					</dd>
				</div>
				<div class="detail-list__item--icon">
					<dt>Shared ownership</dt>
					<dd>
						<bool-icon :value="quote.service_requirements.remortgage.shared_ownership"/>
					</dd>
				</div>
				<div class="detail-list__item--icon">
					<dt>Transfer of equity</dt>
					<dd>
						<bool-icon :value="quote.service_requirements.remortgage.transfer_of_equity"/>
					</dd>
				</div>
			</dl>
			<ul class="actions actions--right">
				<li>
					<button
						class="button button--secondary--small"
						@click.prevent="showInModal('summary')">Back To Summary
					</button>
				</li>
			</ul>
		</v-tab>
	</v-tabs>
</template>
<script>
import Address from "./Address";

export default {
	components: {
		Address,
	},

	props: {
		quote: {
			type: Object,
			required: true,
		},
	},

	methods: {
		showInModal(section) {
			this.$vmodal.open({
				component: () => import("./ConveyancingQuoteBreakdownTabbedModal.vue"),
				props: {
					quote: this.quote,
					section: section,
				},
			});
		},
		feeName(feeIndex) {
			let nameMaps = {
				"disbursement_id_checks": "ID/AML Checks",
			};
			return nameMaps[feeIndex] || feeIndex.toLowerCase()
				.replace("fee_", "")
				.replace("disbursement_", "")
				.split("_")
				.map((s) => s.charAt(0).toUpperCase() + s.substring(1))
				.join(" ");
		},
	},
};
</script>
